import {getLinkData} from 'Application/utils/linkFieldUtils';

import type {LinkField} from 'types/payload-types';

/**
 * A custom hook that extracts and normalizes link data for the `SwmCard` component.
 * This hook processes both the hotline link and the CTA button link, using `getLinkData`
 * to resolve their attributes into a consistent format. It ensures that each link provides
 * the correct `href`, `label`, and (for the button) additional attributes like `rel` and `target`.
 * If either the hotline or button is not defined, their respective values will be `undefined`,
 * making it safe to use in conditional rendering.
 *
 * @param hotline An optional link configuration for the hotline, typically a phone number.
 * @param button  An optional link configuration for the CTA button.
 * @returns An object containing:
 * - `href`: The resolved URL for the hotline link.
 * - `label`: The display label for the hotline link.
 * - `buttonHref`: The resolved URL for the CTA button.
 * - `buttonLabel`: The display label for the CTA button.
 * - `rel`: The `rel` attribute for the button link (if applicable).
 * - `target`: The `target` attribute for the button link (e.g., `_blank` for external links).
 *
 * @example
 * ```tsx
 * const { href, label, buttonHref, buttonLabel } = useSwmCard(
 *   { label: "0800 123 456", linkType: "phone", url: "tel:0800123456" },
 *   { label: "Jetzt beauftragen", linkType: "internal", internal: "/order" }
 * );
 *
 * // href: "tel:0800123456"
 * // label: "0800 123 456"
 * // buttonHref: "/order"
 * // buttonLabel: "Jetzt beauftragen"
 * ```
 */
export const useSwmCard = (hotline?: LinkField, button?: LinkField) => {
    const {href, label} = getLinkData(hotline);
    const {href: buttonHref, label: buttonLabel, rel, target} = getLinkData(button);

    return {
        buttonHref,
        buttonLabel,
        href,
        label,
        rel,
        target
    };
};